import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { FileList } from '../../components/FileList';
import { LeadCut } from '../../components/client/LeadCut';
import { LeadDate } from '../../components/client/LeadDate';
import { Outline } from '../../components/client/Outline';
import { PageDoor } from '../../components/client/PageDoor';
import { PageFoot } from '../../components/client/PageFoot';
import { ProjectTracker } from '../../components/client/ProjectTracker';
import { ReportBody } from '../../components/client/ReportBody';
import { WorkIndex } from '../../components/client/WorkIndex';
import { SECTION_TOP, SectionMark } from '../../components/client/WrapUp';
import { SignOff } from '../../components/client/SignOff';
import { canDownload, fileAction, mediaLink } from '../../utils/deliverables';
import { clientHeadline } from '../../utils/status';
import { toDrivePreviewUrl } from '../../utils/drive';
import { deliverableFromPath, deliverablePath, slugFromPath } from '../../utils/links';
import { fetchWrapUp, showWrapUp, type WrapUp } from '../../utils/wrapUp';
import { fetchProjectSteps, type ProjectStep } from '../../utils/projectSteps';
import { fetchPageBlocks, type BlockKey } from '../../utils/clientPage';
import { fetchClientWords, NO_WORDS } from '../../utils/clientWords';
import { fetchDeliverableDates, latestLine, type DateMap } from '../../utils/deliverableDates';
import { hasOpenableWork, planSummary, stageNow, workSummary } from '../../utils/clientDoor';
import { blockOn, fileRow, leadPick, waitingOn, workTitle } from '../../utils/clientView';
import { shootIsUpcoming } from '../../utils/projectShoots';
import type { Deliverable, FileRow } from '../../types/portal';

/**
 * THE CLIENT PAGE — two halves, joined by a door.
 *
 *   THE WORK  things they act on: the note, the cut playing, approve or ask
 *             for a change, the other films, the downloads, who to ask.
 *   THE PLAN  things they consult: the outline, the tracker, the dates.
 *
 * **Which one they land on is derived, never set.** Is there at least one
 * thing the client can open? Yes → the work leads. No → the plan leads, and
 * there is no door, because there is nowhere to go. The first cut appears and
 * the landing page flips on its own, so nobody has to remember to flip it.
 * Tom: *"as soon as there's something for them to see, that has to become the
 * first page they land on."*
 *
 * **Nothing they must act on ever goes behind the door.** Approvals and
 * change requests are on the work page, which is the page they land on the
 * moment either exists. The door's own summary line carries the other half's
 * state for a client who never clicks it.
 *
 * A finished project is never split: the report is the page, and a tracker of
 * finished steps is history rather than a second half. Same for a project
 * with only one half — it renders as one page, exactly as before the split.
 *
 * What is on the page at all is the producer's choice (`page_blocks`, ticked
 * in setup). Null means every project that predates the flow, and null falls
 * back to the rules the page has always used, so no live page changed the day
 * it shipped.
 *
 * `pages/client/Overview.tsx` is the page this replaces. Still on disk,
 * unreferenced, because it is larger than a single write can carry.
 */
export function ProjectPage() {
  const {
    activeProject,
    clientDeliverables,
    finalDeliverables,
    projectComplete
  } = usePortal();
  const [focusId, setFocusId] = useState(() => typeof window === 'undefined' ? '' : deliverableFromPath(window.location.pathname));
  const [wrap, setWrap] = useState<WrapUp | null>(null);
  const [steps, setSteps] = useState<ProjectStep[]>([]);
  const [blocks, setBlocks] = useState<BlockKey[] | null>(null);
  const [words, setWords] = useState(NO_WORDS);
  const [dates, setDates] = useState<DateMap>(() => new Map());
  const [turned, setTurned] = useState(false);
  const stage = useRef<HTMLDivElement>(null);

  /* Transcripts are never tiles of their own — they ride beside the cut they
     belong to. See LeadCut. */
  const items = useMemo(() => clientDeliverables.filter(item => item.type !== 'Transcript'), [clientDeliverables]);
  const projectId = activeProject.id;
  useEffect(() => {
    if (!projectComplete || !projectId) {
      setWrap(null);
      return;
    }
    let live = true;
    void fetchWrapUp(projectId).then(result => {
      if (live) setWrap(result);
    });
    return () => {
      live = false;
    };
  }, [projectComplete, projectId]);
  useEffect(() => {
    if (!projectId) {
      setSteps([]);
      setBlocks(null);
      setWords(NO_WORDS);
      setDates(new Map());
      return;
    }
    let live = true;
    void Promise.all([fetchProjectSteps(projectId), fetchPageBlocks(projectId), fetchClientWords(projectId), fetchDeliverableDates(projectId)]).then(([rows, ticked, said, when]) => {
      if (!live) return;
      setSteps(rows);
      setBlocks(ticked);
      setWords(said);
      setDates(when);
    });
    return () => {
      live = false;
    };
  }, [projectId]);
  const shoots = useMemo(() => [...activeProject.shoots].filter(shoot => shootIsUpcoming(shoot)).sort((a, b) => a.date.localeCompare(b.date)), [activeProject.shoots]);
  const on = (key: BlockKey) => blockOn(blocks, key, clientDeliverables, steps.length, activeProject.shoots.length, projectComplete);
  const wrapped = projectComplete && showWrapUp(wrap);
  const headline = clientHeadline(activeProject, clientDeliverables, projectComplete);

  /* Their own choice first, then whatever is waiting on them. */
  const focus: Deliverable | undefined = useMemo(() => {
    const chosen = items.find(item => item.id === focusId);
    return chosen ?? leadPick(items);
  }, [items, focusId]);
  const playable = focus ? focus.type === 'Photography' || Boolean(toDrivePreviewUrl(mediaLink(focus))) : false;
  const lead: 'report' | 'cut' | 'date' = projectComplete ? 'report' : playable ? 'cut' : 'date';
  const waiting = lead === 'cut' ? waitingOn(items) : '';
  const inIndex = useMemo(() => new Set(items.map(item => item.id)), [items]);
  const extras = finalDeliverables.filter(item => canDownload(item.status) && !inIndex.has(item.id));
  const downloadRows: FileRow[] = useMemo(() => {
    if (!on('downloads')) return [];
    const source = projectComplete ? [...items, ...extras] : extras;
    return source.filter(item => canDownload(item.status)).map(item => fileRow(item, fileAction(item)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectComplete, items, extras, blocks, steps.length]);
  const hasOutline = on('outline') && Boolean(words.outline);
  const hasTracker = on('plan') && steps.length > 0;
  const hasTiles = items.length > 1 && (on('films') || on('photographs'));

  /* The two halves, and whether there are two of them. A finished project is
     one page whatever it contains. */
  const workSide = hasOpenableWork(items);
  const planSide = hasOutline || hasTracker;
  const split = !projectComplete && workSide && planSide;
  const page: 'work' | 'plan' = !split ? workSide ? 'work' : 'plan' : turned ? 'plan' : 'work';
  const here = (side: 'work' | 'plan') => !split || page === side;
  const showOutline = lead !== 'report' && hasOutline && here('plan');
  const showTracker = lead !== 'report' && hasTracker && here('plan');
  const showTiles = lead !== 'report' && hasTiles && here('work');
  const showFiles = downloadRows.length > 0 && here('work');
  const leadsWithDate = lead === 'date' && on('dates') && !showTracker && !showOutline;
  const latest = lead === 'report' || page === 'plan' ? '' : latestLine(items, dates);
  const sectionKeys = (lead === 'report' ? [wrapped && wrap.quotes.length > 0 ? 'said' : '', wrapped && wrap.next ? 'next' : '', showFiles ? 'files' : ''] : [showOutline ? 'outline' : '', showTracker ? 'plan' : '', showTiles ? 'work' : '', showFiles ? 'files' : '']).filter(Boolean);
  const markOf = (key: string) => {
    const at = sectionKeys.indexOf(key);
    return at < 0 ? '' : String(at + 1).padStart(2, '0');
  };
  function turn(to: 'work' | 'plan') {
    setTurned(to === 'plan');
    if (typeof window !== 'undefined') window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  function pick(id: string) {
    setFocusId(id);
    if (typeof window !== 'undefined') {
      const current = slugFromPath(window.location.pathname);
      if (current) {
        window.history.replaceState(window.history.state, '', `${deliverablePath(current, id)}${window.location.search}`);
      }
    }
    stage.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
  return <div data-id="element-2087" data-mp="3c8cllcu2rhta">
      <p className="label text-mute" data-id="element-2088" data-mp="3c8mdx72le125">{activeProject.client}</p>
      <h1 className="mt-3 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2089" data-mp="325qphug4rmrz">
        {activeProject.name}
      </h1>

      {latest && <p className="label mt-5 text-graphite" data-id="element-2090" data-mp="36xqn3g8qa3le">{latest}</p>}

      {on('note') && words.note && here('work') && <p className="mt-7 max-w-[52ch] text-[1.05rem] font-light leading-relaxed text-graphite" data-id="element-2091" data-mp="2s945f7vmozr0">
          {words.note}
        </p>}

      <div ref={stage} className="scroll-mt-8" data-id="element-2092" data-mp="3kvf8cp6pjcqz">
        {lead === 'report' ? wrapped ? <ReportBody wrap={wrap} saidMark={markOf('said')} nextMark={markOf('next')} data-id="element-2093" data-mp="3pvl168opr5an" /> : <p className="mt-12 max-w-[40ch] font-display text-3xl font-light leading-snug text-charcoal" data-id="element-2094" data-mp="3ayk8o2yp76b4">
              {activeProject.name} is complete. Everything is below, ready to keep.
            </p> : lead === 'cut' && focus && here('work') ? <>
            {waiting && <div className="mt-12 bg-ink px-8 py-10 text-paper sm:px-12" data-id="element-2095" data-mp="24ii19roqvzma">
                <p className="label text-paper/55" data-id="element-2096" data-mp="1om0qvx299c95">Waiting on you</p>
                <p className="mt-5 max-w-[40ch] font-display text-3xl font-light leading-tight" data-id="element-2097" data-mp="1tldp1dyku5r1">
                  {waiting}
                </p>
              </div>}
            <LeadCut focus={focus} all={clientDeliverables} projectComplete={projectComplete} data-id="element-2098" data-mp="1ew7it61bnf9n" />
          </> : leadsWithDate && here('plan') ? <LeadDate shoots={shoots} status={headline.message} support={headline.support} data-id="element-2099" data-mp="15vjc6we9ch6s" /> : null}
      </div>

      {showOutline && <section className={SECTION_TOP} data-id="element-2100" data-mp="1aw6zp83a2fw9">
          <SectionMark n={markOf('outline')} title="What we're making" data-id="element-2101" data-mp="vy4wiz05rwfj" />
          <div className="mt-10" data-id="element-2102" data-mp="vzzjsjywk0ms">
            <Outline text={words.outline} data-id="element-2103" data-mp="10vwotu4ptkk9" />
          </div>
        </section>}

      {showTracker && <section className={SECTION_TOP} data-id="element-2104" data-mp="2qcpf964x5hwa">
          <SectionMark n={markOf('plan')} title="The plan" data-id="element-2105" data-mp="2q9jqxt446k1m" />
          <div className="mt-10" data-id="element-2106" data-mp="2v9fsllpc9ze0">
            <ProjectTracker steps={steps} clientName={activeProject.client} data-id="element-2107" data-mp="2v8ms32gz464b" />
          </div>
        </section>}

      {showTiles && <section className={SECTION_TOP} data-id="element-2108" data-mp="eosu55kj6js5">
          <SectionMark n={markOf('work')} title={workTitle(items)} data-id="element-2109" data-mp="eq40nd5d25my" />
          <div className="mt-10" data-id="element-2110" data-mp="te8k4jc8ml6s">
            <WorkIndex items={items} focusId={focus?.id ?? ''} projectComplete={projectComplete} dates={dates} onPick={pick} data-id="element-2111" data-mp="tei3vp6ovw41" />
          </div>
        </section>}

      {showFiles && <section className={SECTION_TOP} data-id="element-2112" data-mp="ybi5nnwiacpc">
          <SectionMark n={markOf('files')} title={projectComplete ? 'Take it all' : 'Also included'} data-id="element-2113" data-mp="yemg330ke4og" />
          <div className="mt-8" data-id="element-2114" data-mp="17cyis3epgsx9">
            <FileList rows={downloadRows} data-id="element-2115" data-mp="17cormchxcfpz" />
          </div>
        </section>}

      {projectComplete ? <SignOff lead={activeProject.team[0]} data-id="element-2116" data-mp="3e47e1owg6tqe" /> : <PageFoot shoots={shoots} hideSchedule={showTracker || !on('dates') || split && page === 'work'} data-id="element-2117" data-mp="394l0fecpjbh2" />}

      {split && page === 'work' && <PageDoor title="The plan" stage={stageNow(steps)} summary={planSummary(steps, shoots)} onOpen={() => turn('plan')} data-id="element-2118" data-mp="1d3jbhi7xsgsn" />}

      {split && page === 'plan' && <PageDoor title={workTitle(items)} summary={workSummary(items, dates)} onOpen={() => turn('work')} data-id="element-2119" data-mp="1r0pnmty5nh8w" />}
    </div>;
}